import axios from "axios";
import { useEffect, useState } from "react";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";
import { baseURL } from "../../../config/api";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import CustomFormatDatePicker from "../../../components/CustomFormatDatePicker";
import useAuth from "../../../hooks/useAuth";

export const EditGratuityRule = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { auth }: any = useAuth();

    const [ruleData, setRuleData] =
        useState<any>(null);

    const [vehicleTypeId, setVehicleTypeId] =
        useState<string>("");

    const [calculationType, setCalculationType] =
        useState<string>("percentage");

    const [calculationBase, setCalculationBase] =
        useState<string>("base_cost");

    const [percentage, setPercentage] =
        useState<string>("0");

    const [amount, setAmount] =
        useState<string>("0");

    const [isOptional, setIsOptional] =
        useState<string>("Yes");

    const [isTaxable, setIsTaxable] =
        useState<string>("No");

    const [priority, setPriority] =
        useState<string>("0");

    const [validFrom, setValidFrom] =
        useState<string>("");

    const [validTo, setValidTo] =
        useState<string>("");

    const [enabled, setEnabled] =
        useState<string>("Yes");

    const [description, setDescription] =
        useState<string>("");

    const [pleaseWaitPopup, setPleaseWaitPopup] =
        useState<boolean>(false);

    const [vehicleTypeOptions, setVehicleTypeOptions] =
        useState<any[]>([]);


    useEffect(() => {
        axios
            .get(`${baseURL}vehicle-types/`)
            .then((response) => {
                const vehicleTypes =
                    response.data.data || [];

                setVehicleTypeOptions(
                    vehicleTypes.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }))
                );
            })
            .catch((error) => {
                setVehicleTypeOptions([]);
                console.error(error);
            });
    }, []);


    const formatDateForPicker = (
        date: string
    ) => {
        if (!date) return "";

        const parsedDate =
            new Date(date);

        if (isNaN(parsedDate.getTime())) {
            return "";
        }

        const year =
            parsedDate.getFullYear();

        const month =
            String(
                parsedDate.getMonth() + 1
            ).padStart(2, "0");

        const day =
            String(
                parsedDate.getDate()
            ).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };


    useEffect(() => {
        if (!location?.state) {
            return;
        }

        setPleaseWaitPopup(true);

        axios
            .get(
                `${baseURL}gratuity-rules/${location.state}`
            )
            .then((response) => {
                const rule =
                    response?.data?.data;

                setRuleData(rule);

                setVehicleTypeId(
                    rule?.vehicle_type_id != null
                        ? String(
                            rule.vehicle_type_id
                        )
                        : ""
                );

                setCalculationType(
                    rule?.calculation_type ??
                    "percentage"
                );

                setCalculationBase(
                    rule?.calculation_base ??
                    "base_cost"
                );

                setPercentage(
                    rule?.percentage != null
                        ? String(
                            rule.percentage
                        )
                        : "0"
                );

                setAmount(
                    rule?.amount != null
                        ? String(rule.amount)
                        : "0"
                );

                setIsOptional(
                    rule?.is_optional
                        ? "Yes"
                        : "No"
                );

                setIsTaxable(
                    rule?.is_taxable
                        ? "Yes"
                        : "No"
                );

                setPriority(
                    rule?.priority != null
                        ? String(
                            rule.priority
                        )
                        : "0"
                );

                setValidFrom(
                    rule?.valid_from
                        ? formatDateForPicker(
                            rule.valid_from
                        )
                        : ""
                );

                setValidTo(
                    rule?.valid_to
                        ? formatDateForPicker(
                            rule.valid_to
                        )
                        : ""
                );

                setEnabled(
                    rule?.is_active
                        ? "Yes"
                        : "No"
                );

                setDescription(
                    rule?.description ?? ""
                );

                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error fetching gratuity rule:",
                    error
                );
            });
    }, [location.state]);


    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        const payload = {
            vehicle_type_id: vehicleTypeId
                ? Number(vehicleTypeId)
                : null,

            calculation_type:
                calculationType,

            calculation_base:
                calculationBase,

            percentage:
                calculationType === "percentage"
                    ? Number(percentage)
                    : null,

            amount:
                calculationType === "fixed"
                    ? Number(amount)
                    : null,

            is_optional:
                isOptional === "Yes",

            is_taxable:
                isTaxable === "Yes",

            priority:
                Number(priority),

            valid_from: validFrom
                ? new Date(
                    validFrom
                ).toISOString()
                : null,

            valid_to: validTo
                ? new Date(
                    validTo
                ).toISOString()
                : null,

            is_active:
                enabled === "Yes",

            description:
                description.trim(),

            created_by:
                ruleData?.created_by ||
                auth?.givenname,
        };

        await axios
            .put(
                `${baseURL}gratuity-rules/${location.state}`,
                payload,
                {
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    withCredentials: false,
                }
            )
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error updating gratuity rule:",
                    error
                );
            });
    };


    return (
        <>
            <div className="px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto">

                <div className="w-full bg-white p-4 rounded-md">

                    <h2 className="text-lg font-semibold">
                        Edit Gratuity Rule
                    </h2>

                    <div className="mt-2 p-2">

                        <form onSubmit={handleSubmit}>

                            <div className="w-full">


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Vehicle Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={vehicleTypeId}
                                            onChange={(e) =>
                                                setVehicleTypeId(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            {vehicleTypeOptions?.map(
                                                (vehicleType: any) => (
                                                    <option
                                                        key={
                                                            vehicleType.value
                                                        }
                                                        value={String(
                                                            vehicleType.value
                                                        )}
                                                    >
                                                        {
                                                            vehicleType.label
                                                        }
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationType}
                                            onChange={(e) =>
                                                setCalculationType(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="percentage">
                                                Percentage
                                            </option>

                                            <option value="fixed">
                                                Fixed
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Base{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationBase}
                                            onChange={(e) =>
                                                setCalculationBase(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="base_cost">
                                                Base Cost
                                            </option>

                                            <option value="driver_cost">
                                                Driver Cost
                                            </option>

                                            <option value="subtotal">
                                                Subtotal
                                            </option>

                                            <option value="service_amount">
                                                Service Amount
                                            </option>
                                        </select>
                                    </div>


                                    {calculationType ===
                                        "percentage" && (
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                    Percentage
                                                    <span className="text-red-500">
                                                        {" "}*
                                                    </span>
                                                </label>

                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="any"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    value={percentage}
                                                    onChange={(e) =>
                                                        setPercentage(
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                        )}


                                    {calculationType ===
                                        "fixed" && (
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                    Amount
                                                    <span className="text-red-500">
                                                        {" "}*
                                                    </span>
                                                </label>

                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="any"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    value={amount}
                                                    onChange={(e) =>
                                                        setAmount(
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                        )}

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Optional
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={isOptional}
                                            onChange={(e) =>
                                                setIsOptional(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="Yes">
                                                Yes
                                            </option>

                                            <option value="No">
                                                No
                                            </option>
                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Taxable
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={isTaxable}
                                            onChange={(e) =>
                                                setIsTaxable(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="No">
                                                No
                                            </option>

                                            <option value="Yes">
                                                Yes
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Priority
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={priority}
                                            onChange={(e) =>
                                                setPriority(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid From
                                        </label>

                                        <CustomFormatDatePicker
                                            value={validFrom}
                                            onChange={(value) => {
                                                setValidFrom(value);
                                            }}
                                            required={true}
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid To
                                        </label>

                                        <CustomFormatDatePicker
                                            minDate={
                                                validFrom ||
                                                undefined
                                            }
                                            value={validTo}
                                            onChange={(value) => {
                                                setValidTo(value);
                                            }}
                                            required={true}
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Active
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={enabled}
                                            onChange={(e) =>
                                                setEnabled(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="Yes">
                                                Yes
                                            </option>

                                            <option value="No">
                                                No
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="mb-4">

                                    <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                        Description
                                    </label>

                                    <textarea
                                        rows={4}
                                        value={description}
                                        onChange={(e) =>
                                            setDescription(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Description"
                                        className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                    />

                                </div>


                                <div className="flex flex-col sm:flex-row gap-4 mt-6">

                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(-1)
                                        }
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-white text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>
                </div>
            </div>

            <PleasewaitPopup
                showPopup={pleaseWaitPopup}
            />
        </>
    );
};